<template>
    <v-container fluid>
        <v-row class="align-center pl-3">
            <!-- Breadcrumbs -->
            <v-breadcrumbs
                :items="[
                    { text: 'Home', disable: false, href: '/#/' },
                    {
                        text: 'Cost Rates',
                        disable: false,
                        href: '/#/costrates'
                    },
                    { text: 'Growth Cost Templates', disable: true }
                ]"
                divider="/"
            ></v-breadcrumbs>
        </v-row>

        <v-row class="ma-0">
            <v-col cols="12" class="pt-0">
                <v-hover>
                    <template v-slot="{ hover }">
                        <v-card :elevation="hover ? 6 : 1">
                            <v-card-title class="pb-0">
                                <v-icon color="primary" class="mx-2"
                                    >euro_symbol</v-icon
                                >Growth Cost Templates
                                <v-spacer></v-spacer>
                                <v-col cols="12" sm="4">
                                    <v-row justify="end" align="end">
                                        <v-text-field
                                            dense
                                            v-model="search"
                                            label="Search"
                                            clearable
                                            hide-details
                                            class="mx-2"
                                        ></v-text-field>
                                    </v-row>
                                </v-col>
                                <v-spacer></v-spacer>
                                <v-btn
                                    v-if="hasPermission('add_plant')"
                                    color="primary"
                                    dark
                                    outlined
                                    rounded
                                    small
                                    class="mx-2"
                                    @click="newTemplateDialog = true"
                                    >New Template</v-btn
                                >
                            </v-card-title>

                            <v-container fluid>
                                <v-data-table
                                    :headers="headers"
                                    :items="
                                        tenantPlantFixedCostTemplates
                                            ? tenantPlantFixedCostTemplates
                                            : []
                                    "
                                    :search="search"
                                    :loading="
                                        $apollo.queries
                                            .tenantPlantFixedCostTemplates
                                            .loading
                                    "
                                    hide-default-footer
                                    disable-pagination
                                >
                                    <template
                                        v-slot:item.node.potSize.potSizeCm="{
                                            item
                                        }"
                                    >
                                        <v-chip small color="primary">
                                            {{ item.node.potSize.potSizeCm }}cm
                                        </v-chip>
                                    </template>
                                    <template
                                        v-slot:item.node.failureRate="{
                                            item
                                        }"
                                    >
                                        {{ item.node.failureRate * 100 }}%
                                    </template>
                                    <template
                                        v-slot:item.node.totalCost="{
                                            item
                                        }"
                                    >
                                        €{{
                                            item.node.totalCost.toLocaleString()
                                        }}
                                    </template>
                                    <template v-slot:item.action="{ item }">
                                        <v-row class="justify-center">
                                            <v-btn
                                                v-if="
                                                    hasPermission(
                                                        'change_plant'
                                                    )
                                                "
                                                color="grey darken-2"
                                                icon
                                                outlined
                                                small
                                                class="ml-1"
                                                @click="
                                                    copyPlantFixedCostTemplate(
                                                        item
                                                    )
                                                "
                                            >
                                                <v-icon small>file_copy</v-icon>
                                            </v-btn>
                                            <v-btn
                                                icon
                                                outlined
                                                small
                                                class="ml-1"
                                                color="primary"
                                                @click="
                                                    openTemplateDialog(
                                                        item.node
                                                    )
                                                "
                                            >
                                                <v-icon small>edit</v-icon>
                                            </v-btn>
                                            <v-btn
                                                icon
                                                outlined
                                                small
                                                color="error"
                                                class="ml-1"
                                                @click="
                                                    onDeletingTemplate(
                                                        item.node
                                                    )
                                                "
                                            >
                                                <v-icon small>delete</v-icon>
                                            </v-btn>
                                        </v-row>
                                    </template>
                                </v-data-table>
                            </v-container>
                        </v-card>
                    </template>
                </v-hover>
            </v-col>
        </v-row>

        <!-- Delete Dialog -->
        <DeleteDialog
            :dialog.sync="deletePlantFixedCostTemplateDialog"
            :item="selectedPlantFixedCostTemplate"
            v-on:confirmed="deleteTemplateConfirmed"
        >
            <template v-slot:title>Delete Selected Templates</template>
            <template v-slot:content
                >Are you sure you want to delete selected templates?
                <br />
            </template>
        </DeleteDialog>

        <!-- Add Dialog -->
        <PlantFixedCostTemplateEditDialog
            :dialog.sync="newTemplateDialog"
            :object.sync="selectedTemplate"
            :isCopy.sync="isCopyPlantFixedCostTemplate"
            v-on:item-created="onTemplateCreated"
        ></PlantFixedCostTemplateEditDialog>
    </v-container>
</template>

<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";
import DeleteDialog from "@/components/base/DeleteDialog.vue";
import PlantFixedCostTemplateEditDialog from "@/components/cost/PlantFixedCostTemplateEditDialog.vue";

export default {
    name: "plant-fixed-cost-templates-page",
    components: { DeleteDialog, PlantFixedCostTemplateEditDialog },

    apollo: {
        tenantPlantFixedCostTemplates: {
            query: gql`
                query tenantPlantFixedCostTemplates {
                    tenantPlantFixedCostTemplates {
                        edges {
                            node {
                                id
                                templateName
                                failureRate
                                totalCost
                                potSize {
                                    id
                                    potSizeCm
                                }
                                plantFixedCostTemplateItemSet(
                                    orderBy: "itemName"
                                ) {
                                    totalCount
                                    edges {
                                        node {
                                            id
                                            itemName
                                            costValue
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables() {
                return {};
            },
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => {
                return data.tenantPlantFixedCostTemplates.edges.sort((a, b) => {
                    // first sort by greenhouse name, then sort by department name
                    if (a.node.potSize.potSizeCm == b.node.potSize.potSizeCm) {
                        return a.node.templateName.toLowerCase() ==
                            b.node.templateName.toLowerCase()
                            ? 0
                            : a.node.templateName.toLowerCase() >
                              b.node.templateName.toLowerCase()
                            ? 1
                            : -1;
                    } else {
                        return a.node.potSize.potSizeCm ==
                            b.node.potSize.potSizeCm
                            ? 0
                            : a.node.potSize.potSizeCm >
                              b.node.potSize.potSizeCm
                            ? 1
                            : -1;
                    }
                });
            },
            skip: false
            // pollInterval: 60000 // ms
        }
    },

    data() {
        return {
            newTemplateDialog: false,
            isCopyPlantFixedCostTemplate: false,
            deletePlantFixedCostTemplateDialog: false,
            isDeletingPlantFixedCostTemplate: false,
            selectedPlantFixedCostTemplate: null,
            selectedTemplate: null,
            search: null,
            headers: [
                {
                    text: "PotSize",
                    align: "start",
                    sortable: true,
                    value: "node.potSize.potSizeCm"
                },
                {
                    text: "Template Name",
                    align: "start",
                    sortable: true,
                    value: "node.templateName"
                },
                { text: "Failure%", value: "node.failureRate", align: "end" },
                {
                    text: "Cost (Per Plant)",
                    value: "node.totalCost",
                    align: "end"
                },
                {
                    text: "#Items",
                    value: "node.plantFixedCostTemplateItemSet.totalCount",
                    align: "end"
                },
                {
                    text: "Actions",
                    value: "action",
                    width: "120px",
                    align: "center",
                    sortable: false
                }
            ],
            rules: {
                required: v => !helper.isEmpty(v) || "This field is required",
                listRequired: v =>
                    (v || "").length >= 1 || `This field is required`,
                minLength: len => v =>
                    (v || "").length >= len || `Require at least ${len}`,
                maxLength: len => v => (v || "").length <= len || "Too long"
            }
        };
    },
    computed: {},
    watch: {},
    created() {
        this.hasPermission = helper.hasPermission;
    },
    methods: {
        refreshPlantFixedCostTemplates() {
            this.$apollo.queries.tenantPlantFixedCostTemplates.refresh();
        },

        copyPlantFixedCostTemplate(item) {
            this.isCopyPlantFixedCostTemplate = true;
            this.selectedTemplate = item.node;
            this.newTemplateDialog = true;
        },

        onTemplateCreated() {
            this.refreshPlantFixedCostTemplates();
        },

        openTemplateDialog(template) {
            this.selectedTemplate = template;
            this.newTemplateDialog = true;
        },

        onDeletingTemplate(template) {
            this.selectedPlantFixedCostTemplate = template;
            this.deletePlantFixedCostTemplateDialog = true;
        },

        deleteTemplateConfirmed() {
            this.isDeletingPlantFixedCostTemplate = true;

            // console.log(_operatingCostList);

            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation deletePlantFixedCostTemplate(
                            $input: DeletePlantFixedCostTemplateInput!
                        ) {
                            deletePlantFixedCostTemplate(input: $input) {
                                plantFixedCostTemplate {
                                    id
                                    templateName
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: {
                            id: this.selectedPlantFixedCostTemplate.id
                        }
                    }
                })
                .then(() => {
                    this.selectedPlantFixedCostTemplate = null;
                    this.refreshPlantFixedCostTemplates();

                    // show snackbar
                    const payload = {
                        color: "success",
                        message: `Deleted!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .catch(error => {
                    // this.isLoadingPlannedTaskPhases = false;

                    // Error
                    console.error(error);

                    // show snackbar
                    const payload = {
                        color: "error",
                        message: `Failed!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .finally(() => {
                    this.isDeletingPlantFixedCostTemplate = false;
                    this.deletePlantFixedCostTemplateDialog = false;
                });
        }
    }
};
</script>